import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl, fileApiUrl } from '../../config/apiConfig';
import apiUploads from '../../services/apiUploads';
import './style.css';

import { useColumns } from '../../contexts/columnsContext';
import LogoFaktory from '../../assets/logo-faktory.png';
import { MdAssignmentReturned } from "react-icons/md";


const Faktory = ({ cardData }) => {
  const { setShowFaktoryModule } = useColumns();
  const [showNecessidadeEstoque, setShowNecessidadeEstoque] = useState(false);
  const [necessidadeEstoque, setNecessidadeEstoque] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [participanteFaktory, setParticipanteFaktory] = useState({});
  const [pedidoVendaFaktory, setPedidoVendaFaktory] = useState({});
  const [historicoPedidoVendaFaktory, setHistoricoPedidoVendaFaktory] = useState([]);
  const [itensPedidoVendaFaktory, setItensPedidoVendaFaktory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [adiantamentosFaktory, setAdiantamentosFaktory] = useState([]);
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [filtrarPedidoAtual, setFiltrarPedidoAtual] = useState(true);

  // Função para expandir/recolher grupo de adiantamento
  const toggleGrupo = (documentoId) => {
    setGruposExpandidos(prev => ({
      ...prev,
      [documentoId]: !prev[documentoId]
    }));
  };

  // Função para agrupar adiantamentos por documento
  const agruparAdiantamentosPorDocumento = (adiantamentos) => {
    if (!adiantamentos || !Array.isArray(adiantamentos)) return [];
    
    const grupos = {};
    adiantamentos.forEach(adiantamento => {
      // Usar chave composta: CD_DOCUMENTO + NM_DOCUMENTO para identificar unicamente cada documento
      const cdDocumento = adiantamento.CD_DOCUMENTO || 'SEM_DOCUMENTO';
      const nmDocumento = adiantamento.NM_DOCUMENTO || 'N/A';
      const chaveUnica = `${cdDocumento}_${nmDocumento}`;
      
      if (!grupos[chaveUnica]) {
        grupos[chaveUnica] = {
          chaveUnica: chaveUnica,
          documento: cdDocumento,
          nomeDocumento: nmDocumento,
          itens: [],
          totalProvisao: 0,
          totalRecebido: 0,
          totalSaldo: 0
        };
      }
      grupos[chaveUnica].itens.push(adiantamento);
      grupos[chaveUnica].totalProvisao += adiantamento.VL_PROVISAO || 0;
      grupos[chaveUnica].totalRecebido += adiantamento.VL_RECEBIDO || 0;
      grupos[chaveUnica].totalSaldo += adiantamento.VL_SALDO || 0;
    });
    
    return Object.values(grupos);
  };

  // Filtrar adiantamentos baseado no checkbox
  const getAdiantamentosFiltrados = () => {
    if (!filtrarPedidoAtual || !pedidoVendaFaktory || !pedidoVendaFaktory.NM_DOCUMENTO) {
      // Se não está filtrando ou não tem número do documento do pedido, retorna todos
      return adiantamentosFaktory;
    }
    
    // Filtra apenas os adiantamentos cujo NM_DOCUMENTO corresponde ao número do documento do pedido atual
    const numeroDocumentoPedido = pedidoVendaFaktory.NM_DOCUMENTO.toString();
    return adiantamentosFaktory.filter(adiantamento => {
      return adiantamento.NM_DOCUMENTO && adiantamento.NM_DOCUMENTO.toString() === numeroDocumentoPedido;
    });
  };

  // Calcular totalizadores gerais de adiantamentos
  const calcularTotaisAdiantamentos = () => {
    const adiantamentosFiltrados = getAdiantamentosFiltrados();
    const total = adiantamentosFiltrados.reduce((acc, item) => acc + (item.VL_PROVISAO || 0), 0);
    const recebido = adiantamentosFiltrados.reduce((acc, item) => acc + (item.VL_RECEBIDO || 0), 0);
    const saldo = adiantamentosFiltrados.reduce((acc, item) => acc + (item.VL_SALDO || 0), 0);
    return { total, recebido, saldo };
  };

  // Formatar data com validação
  const formatarData = (data) => {
    if (!data) return 'N/A';
    try {
      const dataObj = new Date(data);
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      return dataObj.toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  // Formatar moeda com validação
  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || isNaN(valor)) return 'R$ 0,00';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Função para determinar o tipo de documento baseado no produto
  const getTipoDocumentoByProduto = (produto) => {
    if (!produto) return 'PV'; // Padrão se não houver produto

    const produtoLower = produto.toLowerCase();

    if (produtoLower.trim().startsWith('pvsc')) return 'PVSC';
    if (produtoLower.includes('esquadria')) return 'PV';
    if (produtoLower.includes('bwood')) return 'PVB';
    if (produtoLower.includes('rodapé')) return 'PVR';
    if (produtoLower.includes('porta interna')) return 'PVPI';
    if (produtoLower.includes('perfis')) return 'PVW';
    if (produtoLower.includes('amostras')) return 'AM';
    if (produtoLower.includes('acessórios')) return 'PVN';
    if (produtoLower.includes('retrabalho')) return 'RE';
    if (produtoLower.includes('assistência técnica')) return 'PVAT';

    return 'PV'; // Padrão para outros casos
  };

  useEffect(() => {
    const moduloFaktory = async () => {
      try {
        const tipoDocumento = getTipoDocumentoByProduto(cardData.produto);
        console.log('Tipo de documento:', tipoDocumento);

        const pedidoVendaResponse = await apiUploads.get(`/faktory/pedidovenda/${parseInt(cardData.pedido_number)}?tipoDocumento=${tipoDocumento}`);
        setPedidoVendaFaktory(pedidoVendaResponse.data);

        const participanteResponse = await apiUploads.get(`/faktory/participante/${parseInt(cardData.pedido_number)}?tipoDocumento=${tipoDocumento}`);
        setParticipanteFaktory(participanteResponse.data);

        const historicoPedidoVendaResponse = await apiUploads.get(`/faktory/pedidovendahistorico/${parseInt(cardData.pedido_number)}?tipoDocumento=${tipoDocumento}`);
        setHistoricoPedidoVendaFaktory(historicoPedidoVendaResponse.data);

        const itensPedidoVendaResponse = await apiUploads.get(`/faktory/pedidovendaitem/${parseInt(cardData.pedido_number)}?tipoDocumento=${tipoDocumento}`);
        setItensPedidoVendaFaktory(itensPedidoVendaResponse.data);

        const freteInstalacaoResponse = await apiUploads.get(`/faktory/frete-instalacao/${parseInt(cardData.pedido_number)}/0000?tipoDocumento=${tipoDocumento}`);
        setFreteInstalacao(freteInstalacaoResponse.data);

        const adiantamentosResponse = await apiUploads.get(`/faktory/adiantamentos/${parseInt(cardData.pedido_number)}?tipoDocumento=${tipoDocumento}`);
        setAdiantamentosFaktory(adiantamentosResponse.data);

      } catch (error) {
        console.error('Erro ao buscar informações:', error);
      }
    };

    if (cardData.pedido_number) {
      moduloFaktory();
    }
  }, [cardData.pedido_number, cardData.produto]);

  const [freteInstalacao, setFreteInstalacao] = useState({});

  const buscarNecessidadeEstoque = async () => {
    try {
      const response = await apiUploads.get(`/faktory/necessidade-estoque/${cardData.pedido_number}`);
      setNecessidadeEstoque(response.data);
      setSelectedItems([]); // Limpa seleções anteriores
      setShowNecessidadeEstoque(true);
    } catch (error) {
      console.error('Erro ao buscar necessidade de estoque:', error);
    }
  };

  // Funções para controle de seleção
  const handleSelectAll = () => {
    setSelectedItems(necessidadeEstoque.map((_, index) => index));
  };

  const handleUnselectAll = () => {
    setSelectedItems([]);
  };

  const handleDesconsiderarEspeciais = () => {
    setSelectedItems(prev => {
      return prev.filter(index => {
        const item = necessidadeEstoque[index];
        const codigo = item.CD_PRODUTO;
        // Desmarca itens com códigos especiais
        const codigosEspeciais = ['W-1051', 'W-9053', 'W-2575', 'W-3485', 'W-5456', 'W-8331', 'W-8357'];
        return !codigosEspeciais.includes(codigo);
      });
    });
  };

  const handleItemSelect = (index) => {
    setSelectedItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleBaixarMateriais = async () => {
    if (selectedItems.length === 0) {
      alert('Selecione pelo menos um item para baixar');
      return;
    }

    try {
      setIsLoading(true);
      const itensSelecionados = selectedItems.map(index => necessidadeEstoque[index]);

      await apiUploads.post('/faktory/baixar-necessidade', itensSelecionados);

      alert('Materiais baixados com sucesso');
      setShowNecessidadeEstoque(false);
      setSelectedItems([]);
    } catch (error) {
      console.error('Erro ao baixar materiais:', error);
      alert('Erro ao baixar materiais');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='faktory-modal'>
      <div className='header-faktory-container'>
        <div></div>
        <label className='title-faktory-hader'>SyncsCRM</label>
        <div className='btn-necessidade-estoque-container'>
          <button onClick={buscarNecessidadeEstoque} className='btn-necessidade-estoque'>
            <MdAssignmentReturned className='icon-necessidade-estoque' />
          </button>
          <button onClick={() => setShowFaktoryModule(false)} className='btn-close-faktory-module'>x</button>
        </div>

      </div>

      <div className='body-faktory-container'>
        <div className='faktory-container-pedido-venda'>
          <h3 className='title-faktory-module-container'>
            <span className='icon-title'>📋</span> Dados do Pedido de Venda
          </h3>

          <div className='pedido-venda-faktory-container'>
            {pedidoVendaFaktory && Object.keys(pedidoVendaFaktory).length > 0 ? (
              <div className='info-card-grid'>
                <div className='info-card'>
                  <label>Documento</label>
                  <span>{pedidoVendaFaktory.NM_DOCUMENTO || 'N/A'}</span>
                </div>

                <div className='info-card'>
                  <label>Endereço</label>
                  <span>{pedidoVendaFaktory.DS_ENDERECO || 'N/A'}</span>
                </div>

                <div className='info-card full-width'>
                  <label>Observação</label>
                  <span>{pedidoVendaFaktory.DS_OBSERVACAO || 'N/A'}</span>
                </div>

                <div className='info-card destaque'>
                  <label>Frete</label>
                  <span className='valor-destaque'>{formatarMoeda(freteInstalacao.VL_TOTALFRETE)}</span>
                </div>
                
                <div className='info-card destaque'>
                  <label>Instalação</label>
                  <span className='valor-destaque'>{formatarMoeda(freteInstalacao.VL_TOTALINSTALACAO)}</span>
                </div>

                <div className='info-card destaque'>
                  <label>Valor Total Pedido</label>
                  <span className='valor-destaque'>{formatarMoeda(pedidoVendaFaktory.VALORTOTALPV)}</span>
                </div>

                <div className='info-card destaque'>
                  <label>Valor Total Financeiro</label>
                  <span className='valor-destaque'>{formatarMoeda(pedidoVendaFaktory.VALORTOTALFINANCEIRO)}</span>
                </div>
              </div>
            ) : (
              <div className='sem-dados'>Nenhum dado do pedido encontrado</div>
            )}
          </div>
        </div>

        <div className='faktory-container-pedido-venda adiantamentos-section'>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 className='title-faktory-module-container' style={{ marginBottom: 0 }}>
              <span className='icon-title'>💰</span> Adiantamentos
            </h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#2d3748' }}>
              <input
                type="checkbox"
                checked={filtrarPedidoAtual}
                onChange={(e) => setFiltrarPedidoAtual(e.target.checked)}
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              />
              <span>Mostrar apenas do pedido atual</span>
            </label>
          </div>
          
          {/* Totalizadores Gerais */}
          {getAdiantamentosFiltrados() && getAdiantamentosFiltrados().length > 0 && (
            <div className='totalizadores-gerais'>
              <div className='totalizador-card total'>
                <div className='totalizador-label'>Total Geral</div>
                <div className='totalizador-valor'>{formatarMoeda(calcularTotaisAdiantamentos().total)}</div>
              </div>
              <div className='totalizador-card pago'>
                <div className='totalizador-label'>Total Pago</div>
                <div className='totalizador-valor'>{formatarMoeda(calcularTotaisAdiantamentos().recebido)}</div>
              </div>
              <div className='totalizador-card saldo'>
                <div className='totalizador-label'>Saldo a Receber</div>
                <div className='totalizador-valor'>{formatarMoeda(calcularTotaisAdiantamentos().saldo)}</div>
              </div>
            </div>
          )}

          <div className='pedido-venda-faktory-container'>
            {getAdiantamentosFiltrados() && getAdiantamentosFiltrados().length > 0 ? (
              agruparAdiantamentosPorDocumento(getAdiantamentosFiltrados()).map((grupo, groupIndex) => {
                const isExpanded = gruposExpandidos[grupo.chaveUnica];
                return (
                  <div key={groupIndex} className='grupo-documento'>
                    <div className='documento-header'>
                      <div className='documento-header-top'>
                        <h4 className='documento-titulo'>
                          📄 Documento: {grupo.documento} - {grupo.nomeDocumento}
                        </h4>
                        <button 
                          className='btn-toggle-grupo'
                          onClick={() => toggleGrupo(grupo.chaveUnica)}
                          title={isExpanded ? 'Recolher parcelas' : 'Expandir parcelas'}
                        >
                          {isExpanded ? '▼' : '▶'} {isExpanded ? 'Ocultar' : 'Mostrar'} Parcelas ({grupo.itens.length})
                        </button>
                      </div>
                      <div className='documento-totais'>
                        <span className='doc-total'>Total: {formatarMoeda(grupo.totalProvisao)}</span>
                        <span className='doc-pago'>Pago: {formatarMoeda(grupo.totalRecebido)}</span>
                        <span className='doc-saldo'>Saldo: {formatarMoeda(grupo.totalSaldo)}</span>
                      </div>
                    </div>

                    {/* Barra de progresso */}
                    <div className='progress-bar-container'>
                      <div 
                        className='progress-bar-fill' 
                        style={{ 
                          width: `${grupo.totalProvisao > 0 ? (grupo.totalRecebido / grupo.totalProvisao) * 100 : 0}%` 
                        }}
                      >
                        <span className='progress-text'>
                          {grupo.totalProvisao > 0 ? `${((grupo.totalRecebido / grupo.totalProvisao) * 100).toFixed(1)}%` : '0%'}
                        </span>
                      </div>
                    </div>

                    {/* Parcelas - Mostrar somente se expandido */}
                    {isExpanded && (
                      <div className='parcelas-container'>
                        {grupo.itens.map((adiantamento, index) => (
                          <div key={index} className='adiantamento-card'>
                            <div className='adiantamento-grid'>
                              <div className='adiantamento-field'>
                                <label>Parcela</label>
                                <span className='parcela-badge'>{adiantamento.NM_PARCELA || 'N/A'}</span>
                              </div>
                              
                              <div className='adiantamento-field'>
                                <label>Emissão</label>
                                <span>{formatarData(adiantamento.DT_EMISSAO)}</span>
                              </div>
                              
                              <div className='adiantamento-field'>
                                <label>Vencimento</label>
                                <span>{formatarData(adiantamento.DT_VENCIMENTO)}</span>
                              </div>
                              
                              <div className='adiantamento-field'>
                                <label>Meio Pagamento</label>
                                <span>{adiantamento.DS_MEIOPAGAMENTO || 'N/A'}</span>
                              </div>
                              
                              <div className='adiantamento-field destaque'>
                                <label>Valor</label>
                                <span className='valor-total'>{formatarMoeda(adiantamento.VL_PROVISAO)}</span>
                              </div>
                              
                              <div className='adiantamento-field destaque'>
                                <label>Recebido</label>
                                <span className='valor-recebido'>{formatarMoeda(adiantamento.VL_RECEBIDO)}</span>
                              </div>
                              
                              <div className='adiantamento-field destaque'>
                                <label>Saldo</label>
                                <span className='valor-saldo'>{formatarMoeda(adiantamento.VL_SALDO)}</span>
                              </div>
                              
                              <div className='adiantamento-field'>
                                <label>Status</label>
                                <span className={`status-badge status-${(adiantamento.STATUS || 'pendente').toLowerCase().replace(/\s/g, '-')}`}>
                                  {adiantamento.STATUS || 'Pendente'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className='sem-dados'>Nenhum adiantamento encontrado</div>
            )}
          </div>
        </div>

        <div className='faktory-container-pedido-venda'>
          <h3 className='title-faktory-module-container'>
            <span className='icon-title'>📝</span> Histórico do Pedido de Venda
          </h3>
          <div className='pedido-venda-faktory-container'>
            {historicoPedidoVendaFaktory && historicoPedidoVendaFaktory.length > 0 ? (
              historicoPedidoVendaFaktory.map((historico, index) => (
                <div key={index} className='historico-item'>
                  <div className='historico-bullet'>•</div>
                  <div className='historico-text'>
                    {historico.DS_HISTORICO || 'N/A'}
                  </div>
                </div>
              ))
            ) : (
              <div className='sem-dados'>Nenhum histórico encontrado</div>
            )}
          </div>
        </div>

        <div style={{ display: 'none' }} className='faktory-container-pedido-venda'>
          <h3 className='title-faktory-module-container'>Itens do Pedido de Venda</h3>
          <div className='pedido-venda-faktory-container'>
            {itensPedidoVendaFaktory && itensPedidoVendaFaktory.map((item, index) => (
              <div key={index} className='pedido-venda-item'>
                <div>
                  <label>QUANTIDADE:</label> {item.QT_PRODUTOCOM !== null ? item.QT_PRODUTOCOM : 'N/A'}
                </div>
                <div>
                  <label>DESCRIÇÃO:</label> {item.DS_COMPLEMENTAR !== null ? item.DS_COMPLEMENTAR : 'N/A'}
                </div>
                <div>
                  <label>VALOR:</label> {item.VL_UNITARIOCOM !== null ? item.VL_UNITARIOCOM.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='faktory-container-pedido-venda'>
          <h3 className='title-faktory-module-container'>
            <span className='icon-title'>👤</span> Dados do Participante
          </h3>
          <div className='pedido-venda-faktory-container'>
            {participanteFaktory && Object.keys(participanteFaktory).length > 0 ? (
              <div className='info-card-grid'>
                <div className='info-card full-width destaque-primary'>
                  <label>Razão Social</label>
                  <span>{participanteFaktory.DS_RAZAOSOCIAL || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Nome Fantasia</label>
                  <span>{participanteFaktory.DS_NOMEFANTASIA || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>CNPJ</label>
                  <span>{participanteFaktory.NM_CNPJ || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>CEP</label>
                  <span>{participanteFaktory.NM_CEP || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Endereço</label>
                  <span>{participanteFaktory.DS_ENDERECO || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Número</label>
                  <span>{participanteFaktory.NM_ENDERECO || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Complemento</label>
                  <span>{participanteFaktory.DS_COMPLEMENTO || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Bairro</label>
                  <span>{participanteFaktory.DS_BAIRRO || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Telefone 1</label>
                  <span>{participanteFaktory.NM_TELEFONE1 || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Telefone 2</label>
                  <span>{participanteFaktory.NM_TELEFONE2 || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Email</label>
                  <span>{participanteFaktory.DS_WEBSITE || 'N/A'}</span>
                </div>
                
                <div className='info-card'>
                  <label>Contato Principal</label>
                  <span>{participanteFaktory.DS_CONTATOPRINCIPAL || 'N/A'}</span>
                </div>
              </div>
            ) : (
              <div className='sem-dados'>Nenhum dado do participante encontrado</div>
            )}
          </div>
        </div>
      </div>

      <div className='footer-faktory-container'></div>

      {showNecessidadeEstoque && (
        <div className='modal-necessidade-estoque'>
          <div className='header-faktory-container'>
            <label className='title-faktory-hader-left'>Necessidade de Estoque</label>
            <div></div>
            <div className='select-buttons-container'>
                             <button 
                 onClick={handleDesconsiderarEspeciais} 
                 className='btn-desconsiderar-especiais'
                 disabled={selectedItems.length === 0}
                 title="Códigos especiais: W-1051, W-9053, W-2575, W-3485, W-5456, W-8331, W-8357"
               >
                 Desconsiderar Especiais
               </button>
              <button onClick={handleSelectAll} className='btn-select-all'>
                Selecionar Todos
              </button>
              <button onClick={handleUnselectAll} className='btn-unselect-all'>
                Desmarcar Todos
              </button>
              <button
                onClick={handleBaixarMateriais}
                disabled={selectedItems.length === 0 || isLoading}
                className='btn-baixar-materiais'
              >
                {isLoading ? 'Baixando...' : 'Baixar Selecionados'}
              </button>
            </div>
            <button onClick={() => setShowNecessidadeEstoque(false)} className='btn-close-faktory-module'>x</button>
          </div>

          <div className='body-faktory-container'>
            <div className='faktory-container-pedido-venda'>
              <div className='pedido-venda-faktory-container'>
                {necessidadeEstoque.map((item, index) => (
                  <div key={index} className='pedido-venda-item-estoque'>
                    <div className='item-checkbox'>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(index)}
                        onChange={() => handleItemSelect(index)}
                      />
                    </div>

                    <div><label>Código:</label> {item.CD_PRODUTO}</div>
                    <div><label>Produto:</label> {item.DS_PRODUTO}</div>
                    <div><label>Variante:</label> {item.DS_PRODUTOVARIANTE}</div>
                    <div><label>Quantidade:</label> {item.QT_NECESSARIA}</div>



                    <div><label>Pedido:</label> {item.PEDIDOVENDA}</div>
                    <div><label>Projeto:</label> {item.NM_CODIGO}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='footer-faktory-container'></div>
        </div>
      )}
    </div>
  );
};

export default Faktory;
